import { useState, useEffect, FormEvent, useCallback } from 'react';

interface QuickEnquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultService?: string;
}

const services = [
  'YouTube Services',
  'Music Production',
  'Audio Recording',
  'Dubbing & Voice Over',
  'Mixing & Mastering',
  'Video Production',
  'Artist Management',
  'Digital Promotion',
  'Other',
];

export default function QuickEnquiryModal({ isOpen, onClose, defaultService = '' }: QuickEnquiryModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [service, setService] = useState(defaultService);
  const [message, setMessage] = useState('');

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setSubmitted(false);
      setName('');
      setPhone('');
      setEmail('');
      setService(defaultService);
      setMessage('');
    }
  }, [isOpen, defaultService]);

  // Close on Escape
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const text = `New Website Enquiry 🚀

Name: ${name}
Phone: ${phone}
Email: ${email}
Service: ${service || 'Not specified'}
Message: ${message}`;

    const encoded = encodeURIComponent(text);
    const waUrl = `https://wa.me/919990194423?text=${encoded}`;

    setSubmitted(true);
    setTimeout(() => {
      window.open(waUrl, '_blank');
    }, 700);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-lg bg-[#111] border border-[#d4af37]/20 rounded-3xl overflow-hidden shadow-2xl animate-[fadeInUp_0.3s_ease]">
        {/* Header */}
        <div className="relative px-8 pt-8 pb-6 border-b border-white/5">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-green-500/20">
                  <i className="ri-whatsapp-line text-green-400 text-sm"></i>
                </div>
                <span className="text-green-400 text-xs font-semibold tracking-widest uppercase">Quick Enquiry</span>
              </div>
              <h2 className="font-heading text-2xl font-bold text-white">
                Let's Talk <span className="text-[#d4af37]">Business</span>
              </h2>
              <p className="text-white/50 text-sm mt-1">We'll reply on WhatsApp within hours</p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-all cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Gold accent line */}
          <div className="absolute bottom-0 left-8 w-16 h-0.5 bg-[#d4af37]"></div>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-green-500/15 border border-green-500/30 mx-auto mb-5">
                <i className="ri-whatsapp-line text-green-400 text-4xl"></i>
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-2">Opening WhatsApp!</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Your enquiry is ready. WhatsApp is opening with your pre-filled message — just hit send!
              </p>
              <div className="flex gap-3 mt-6 justify-center">
                <button
                  onClick={() => setSubmitted(false)}
                  className="px-5 py-2 rounded-full text-sm text-[#d4af37] border border-[#d4af37]/30 hover:bg-[#d4af37]/10 transition-all cursor-pointer whitespace-nowrap"
                >
                  Send Another
                </button>
                <button
                  onClick={onClose}
                  className="px-5 py-2 rounded-full text-sm text-white bg-white/10 hover:bg-white/15 transition-all cursor-pointer whitespace-nowrap"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/60 text-xs mb-1.5 font-medium">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#d4af37]/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-xs mb-1.5 font-medium">Phone *</label>
                  <input
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 XXXXX XXXXX"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#d4af37]/50 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white/60 text-xs mb-1.5 font-medium">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#d4af37]/50 transition-colors"
                />
              </div>

              <div>
                <label className="block text-white/60 text-xs mb-1.5 font-medium">Service Interested In</label>
                <select
                  value={service}
                  onChange={(e) => setService(e.target.value)}
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#d4af37]/50 transition-colors"
                >
                  <option value="">Select a service</option>
                  {services.map((s) => (
                    <option key={s} value={s} className="bg-[#1a1a1a]">{s}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-white/60 text-xs mb-1.5 font-medium">Message *</label>
                <textarea
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Tell us about your project..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#d4af37]/50 transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                className="w-full py-3.5 rounded-xl text-sm font-semibold text-white whitespace-nowrap bg-green-500 hover:bg-green-600 cursor-pointer transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                Send Enquiry via WhatsApp
              </button>

              <p className="text-center text-white/30 text-xs">
                Sends to Rohit Sharma · 919990194423
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
